import React, { useState, useEffect } from 'react';
import { 
    AppBar, Toolbar, Typography, Drawer, List, ListItem, 
    ListItemIcon, ListItemText, Box, CssBaseline, ListItemButton,
    Button, Divider, Tooltip
} from '@mui/material';
import { Apps, Security, Person, AdminPanelSettings, Logout, DownloadForOffline } from '@mui/icons-material';
import { useNavigate, Outlet } from 'react-router-dom';
import { API } from '../core/API';

const drawerWidth = 240;
const authApi = new API("/api/v1/admins");

const MainLayout = () => {
    const navigate = useNavigate();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallBtn, setShowInstallBtn] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e);
            // Update UI notify the user they can install the PWA
            setShowInstallBtn(true);
        };

        window.addEventListener('beforeinstallprompt', handler);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        
        // Show the install prompt
        deferredPrompt.prompt();
        
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);
        
        // We've used the prompt, and can't use it again, throw it away
        setDeferredPrompt(null);
        setShowInstallBtn(false);
    };

    const menuItems = [
        { text: 'Applications', icon: <Apps />, path: '/applications' },
        { text: 'Users', icon: <Person />, path: '/users' },
        { text: 'Security', icon: <Security />, path: '/security' },
        { text: 'System Admins', icon: <AdminPanelSettings />, path: '/admins' },
    ];

    const handleLogout = async () => {
        try {
            await authApi.post("/logout");
            localStorage.removeItem('adminUser');
            navigate('/login');
        } catch (error) {
            console.error("Logout failed", error);
            // Even if backend fails, clear local state
            localStorage.removeItem('adminUser');
            navigate('/login');
        }
    };

    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" noWrap component="div">
                        Identity Service Admin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {showInstallBtn && (
                            <Tooltip title="Install App for Offline Use">
                                <Button 
                                    variant="outlined"
                                    color="inherit" 
                                    startIcon={<DownloadForOffline />} 
                                    onClick={handleInstallClick}
                                    size="small"
                                    sx={{ borderColor: 'rgba(255,255,255,0.5)', mr: 1 }}
                                >
                                    Install App
                                </Button>
                            </Tooltip>
                        )}
                        <Typography variant="body2">
                            Logged in as: <strong>{adminUser.fullName || 'Admin'}</strong>
                        </Typography>
                        <Button 
                            color="inherit" 
                            startIcon={<Logout />} 
                            onClick={handleLogout}
                            size="small"
                        >
                            Logout
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
                }}
            >
                <Toolbar />
                <Box sx={{ overflow: 'auto' }}>
                    <List>
                        {menuItems.map((item) => (
                            <ListItem key={item.text} disablePadding>
                                <ListItemButton onClick={() => navigate(item.path)}>
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Toolbar />
                <Outlet />
            </Box>
        </Box>
    );
};

export default MainLayout;
